import get from 'lodash-es/get';
import { v4 as uuidv4 } from 'uuid';
import { GwDatesWrapper } from '../../../components/_internal_date_/gwDatesWrapper';
import { getLocation } from '../../../components/utilities/ResortLocations';
import { getTestValueAB, TESTS_DICTIONARY } from '../../../utilities/ABTesting';
import {
  DATE_FORMATS,
  DAY_PASSES_ROOM_TYPE,
  EVENT_CATEGORIES,
  EVENT_PARAMS,
  GTM_EVENT,
  PRODUCT_SCOPES,
  RECOMMENDED_FOR_YOU,
  RESERVATION_TYPE
} from '../../../utilities/constants';
import { getReservationCurrency } from '../../../utilities/reservationsUtils';
import { getSpecificItem, SessionStorageKeys } from '../../../utilities/storageUtils';
import { getPackageCategory, getPackageQuantity, getPkgType, isRecommendedPackage } from './analyticsUtils';

const optimizelyReturnZSRecommendation = getTestValueAB(TESTS_DICTIONARY?.optimizelyReturnZSRecommendation, false);

const applyAgentProperty = (returnObj, upsellRecommendationData) => {
  const packagesZS = Object.keys(upsellRecommendationData?.addons);
  if (packagesZS.includes(returnObj.id)) {
    returnObj.agent = 'ZS-' + upsellRecommendationData?.analyticalGroup;
  } else {
    returnObj.agent = 'GW';
  }
};

// Full enhanced E-Commerce Object
export const buildPurchaseEnhancedECommerceObject = (
  {
    reservation,
    products,
    revenue,
    tax,
    shipping,
    voyagerPointsDiscount,
    offersPointsDiscount,
    dayType,
    UUID,
    upsellRecommendationData
  },
  eventCategory = EVENT_CATEGORIES.GTM.transaction,
  productScope = PRODUCT_SCOPES.suiteCheckout
) => {
  const uniquePackages = [...new Map(products.map(m => [m.id, m])).values()];
  uniquePackages.forEach(unique => {
    unique.dimension63 = reservation.payment.description;
  });

  let returnObj = {
    event: eventCategory,
    resortLocation: getLocation(reservation.lodge.code).url, //custom dimension for resort location that we can easily filter EE events by,
    purchaseLocation: productScope, //custom dimension for transaction location
    ecommerce: {
      currencyCode: getReservationCurrency(reservation.lodge.code),
      purchase: {
        actionField: {
          id: UUID ? UUID : uuidv4(),
          confirmationNumber: reservation?.externalId ?? reservation?.id,
          affiliation: GTM_EVENT.LABEL.greatWolfLowerCase,
          revenue: revenue,
          shipping: shipping ?? 0,
          tax: tax,
          coupon: reservation?.rateCode ?? '', // as requested by the analytics team we are using the rate code as the coupon on the suite product
          voyagerPointsDiscount: voyagerPointsDiscount ?? 0,
          offersPointsDiscount: offersPointsDiscount ?? 0,
          SIP: dayType
        },
        products: uniquePackages
      }
    }
  };
  if (optimizelyReturnZSRecommendation) {
    const hasAddOns = Object.keys(upsellRecommendationData?.addons)?.length > 0;
    const packagesZS = hasAddOns && Object.keys(upsellRecommendationData.addons);
    const ZScase = 'ZS-' + upsellRecommendationData?.analyticalGroup;
    const reservationHasZScase = uniquePackages?.some(unq => unq.agent === ZScase);
    hasAddOns && (reservationHasZScase || packagesZS?.includes(returnObj.id))
      ? (returnObj.ecommerce.purchase.actionField.agent = ZScase)
      : (returnObj.ecommerce.purchase.actionField.agent = 'GW');
  }
  return returnObj;
};

// Enhanced E-Commerce experience for suite
export const buildSuitePurchaseEnhancedECommerceObject = (
  reservation,
  productScope = PRODUCT_SCOPES.suiteCheckout,
  maxOccupancy,
  suiteCategory,
  upsellRecommendationData
) => {
  const checkInDate = get(reservation, 'arrival', '');
  const checkoutDate = get(reservation, 'departure', '');
  const isDayPass = reservation.reservationType.includes('PASS');
  const numberOfNights = GwDatesWrapper.diff(checkoutDate, checkInDate, 'days');
  const isDayPassOrSuite = isDayPass ? RESERVATION_TYPE.DAYPASS : RESERVATION_TYPE.SUITE;

  let returnObj = {
    name: reservation.product.name,
    id: reservation.reservationType,
    price: isDayPass
      ? (reservation.product.totalPrice / reservation.guests.quantity).toFixed(2)
      : reservation.product.totalPrice.toFixed(2),
    brand: 'great wolf',
    category: isDayPassOrSuite,
    variant: isDayPass ? RESERVATION_TYPE.DAYPASS : suiteCategory, // standard, themed, premium, condo, cabin
    quantity: isDayPass ? reservation.guests.quantity : 1,
    coupon: reservation?.rateCode ?? '', // as requested by the analytics team we are using the rate code as the coupon on the suite product
    dimension29: getLocation(reservation.lodge.code).url, // product scope dimension for resort
    dimension30: isDayPassOrSuite, // product scope dimension for category
    dimension31: numberOfNights, // product scope dimension for number of nights
    dimension32: reservation.guests.adults, // product scope dimension for number of adults
    dimension33: reservation.guests.children.length ?? 0, // product scope dimension for number of kids
    dimension34: productScope, // product scope dimension for add to cart location (pre vs post transaction)
    dimension35: GwDatesWrapper.format(checkInDate, DATE_FORMATS.default),
    dimension36: GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default),
    dimension61: maxOccupancy ?? null, // product scope dimension for Maximum Suite Occupancy
    dimension62: reservation.guests.children ?? [] // product scope dimension for Kids Ages
  };
  if (optimizelyReturnZSRecommendation && upsellRecommendationData?.analyticalGroup !== undefined) {
    applyAgentProperty(returnObj, upsellRecommendationData);
  }
  return returnObj;
};

// New enhances E-Commerce experience for packages
export const buildPackagesPurchaseEnhancedECommerceObject = (
  {
    addedPackagesCodes,
    packages,
    groupedAddedPackages,
    reservation,
    packagesContent,
    formatedOffers = [],
    maxOccupancy,
    upsellRecommendationData
  },
  productScope = PRODUCT_SCOPES.postPurchase
) => {
  const checkInDate = get(reservation, 'arrival', '');
  const checkoutDate = get(reservation, 'departure', '');
  const numberOfNights = GwDatesWrapper.diff(checkoutDate, checkInDate, 'days');

  const addedPackagesFormat = addedPackagesCodes.reduce((acc, packageCode) => {
    const packagesByCode = packages.reduce((keysAcc, currentPackage) => {
      keysAcc[currentPackage?.code || currentPackage?.packageCode] = currentPackage;
      return keysAcc;
    }, {});

    const pkgType = getPkgType(reservation?.addOns || packagesContent, packageCode);

    let returnObj = {
      name:
        packagesByCode[packageCode]?.name ||
        packagesByCode[packageCode]?.title ||
        packagesByCode[packageCode]?.description,
      id: packageCode,
      price: groupedAddedPackages[packageCode]?.reduce((revenueAcc, packageItem) => {
        return revenueAcc + packageItem?.unitPrice || packageItem?.unitAmount;
      }, 0),
      brand: GTM_EVENT.LABEL.greatWolfLowerCase,
      category: 'packages',
      variant: pkgType,
      quantity: packageCode?.includes('PARKING') ? numberOfNights : packagesByCode[packageCode]?.quantity,
      coupon: reservation?.rateCode ?? reservation?.offerCode ?? '',
      dimension29: getLocation(reservation?.lodge?.code || reservation?.property).url, //product scope dimension for resort
      dimension30: 'packages', //product scope dimension for category
      dimension31: numberOfNights, //product scope dimension for number of nights
      dimension32: reservation?.guests?.adults || reservation?.selection?.numAdults, //product scope dimension for number of adults
      dimension33: reservation?.guests?.children?.length || reservation?.selection?.numChildren || 0, //product scope dimension for number of kids
      dimension34: productScope, //product scope dimension for add to cart location (pre vs post transaction)
      dimension35: GwDatesWrapper.format(checkInDate, DATE_FORMATS.default),
      dimension36: GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default),
      dimension61: maxOccupancy || null, // product scope dimension for Maximum Suite Occupancy
      dimension62: (reservation?.guests?.children && JSON.stringify(reservation?.guests?.children)) || [] // product scope dimension for Kids Ages
    };
    if (optimizelyReturnZSRecommendation && upsellRecommendationData?.analyticalGroup !== undefined) {
      applyAgentProperty(returnObj, upsellRecommendationData);
    }
    acc.push(returnObj);
    const uniquePackages = [...new Map(acc.map(m => [m.id, m])).values()];
    return uniquePackages;
  }, []);

  if (formatedOffers.length > 0) {
    formatedOffers.forEach(offer => {
      addedPackagesFormat.push({
        name: offer.packageName,
        id: offer.packageCode,
        price: -offer.amount,
        brand: GTM_EVENT.LABEL.greatWolfLowerCase,
        category: offer.category,
        variant: offer.packageCategory,
        quantity: offer.quantity,
        coupon: reservation?.rateCode ?? reservation.offerCode ?? '',
        dimension29: getLocation(reservation?.lodge?.code || reservation.property).url, //product scope dimension for resort
        dimension30: 'packages', //product scope dimension for category
        dimension31: numberOfNights, //product scope dimension for number of nights
        dimension32: reservation?.guests?.adults || reservation.selection.numAdults, //product scope dimension for number of adults
        dimension33: reservation?.guests?.children
          ? reservation.guests.children.length
          : reservation.selection.numChildren, //product scope dimension for number of kids
        dimension34: productScope, //product scope dimension for add to cart location (pre vs post transaction)
        dimension35: GwDatesWrapper.format(checkInDate, DATE_FORMATS.default),
        dimension36: GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default)
      });
    });

    return addedPackagesFormat;
  }

  return addedPackagesFormat;
};

export const buildImpressionsEventObject = (resortLocation, eventProps, purchaseLocation) => ({
  event: PRODUCT_SCOPES.productImpressions,
  resortLocation: resortLocation,
  purchaseLocation: purchaseLocation || null,
  ecommerce: {
    currencyCode: 'USD',
    impressions: Array.isArray(eventProps) ? eventProps : [eventProps]
  }
});

export const buildImpressionsDataObject = (
  kidsAges,
  suite,
  resortLocation,
  checkoutDate,
  checkInDate,
  adultsCount,
  kidsCount,
  currentPosition,
  customPurchaseLocation = null
) => ({
  name: suite.title,
  id: suite.suiteCode,
  price: suite.totalRate,
  brand: suite.suiteBrand,
  category: 'suites',
  variant: suite.suiteCategory,
  list: 'suites',
  position: currentPosition,
  dimension27: suite.flags && suite.flags[RECOMMENDED_FOR_YOU] ? 'recommended' : '',
  dimension28: '',
  dimension29: resortLocation,
  dimension30: 'suites',
  dimension31: GwDatesWrapper.diff(checkoutDate, checkInDate, 'days'),
  dimension32: adultsCount,
  dimension33: kidsCount,
  dimension34: customPurchaseLocation ? customPurchaseLocation : 'suite-checkout',
  dimension35: GwDatesWrapper.format(checkInDate, DATE_FORMATS.default),
  dimension36: GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default),
  dimension61: Number(suite?.selectedSuite?.maxOccupancy) || null, // product scope dimension for Maximum Suite Occupancy
  dimension62: kidsAges || [] // product scope dimension for Kids Ages
});

export const extraGuestPurchaseObj = (reservation, maxOccupancy, suiteCategory, amount, additionalGuests) => {
  const nights = GwDatesWrapper.diff(reservation?.departure, reservation?.arrival, 'days');

  return {
    name: reservation?.roomDescription.split('-')[0],
    id: reservation?.roomType,
    price: amount.toFixed(2),
    brand: 'great wolf',
    category: 'suites',
    variant: suiteCategory,
    quantity: 1,
    dimension27: 'recommended',
    dimension28: 'recommended',
    dimension29: reservation?.property,
    dimension30: 'suites',
    dimension31: nights.toString() + (nights > 1 || nights === 0 ? ' nights' : nights > 0 && ' night'),
    dimension32: !additionalGuests.adultsCount
      ? '0 adults'
      : additionalGuests.adultsCount.toString() +
        (additionalGuests.adultsCount > 1 ? ' adults' : additionalGuests.adultsCount === 1 && ' adult'),
    dimension33: !additionalGuests.childrenAges
      ? '0 kids'
      : additionalGuests.childrenAges.length.toString() +
        (additionalGuests.childrenAges.length > 1 ? ' kids' : additionalGuests.childrenAges.length === 1 && ' kid'),
    dimension34: 'modify reservation',
    dimension35: GwDatesWrapper.format(reservation?.departure, DATE_FORMATS.default),
    dimension36: GwDatesWrapper.format(reservation?.arrival, DATE_FORMATS.default),
    dimension61: Number(maxOccupancy), // product scope dimension for Maximum Suite Occupancy
    dimension62: !additionalGuests.childrenAges ? [] : additionalGuests.childrenAges // product scope dimension for Kids Ages
  };
};

export const buildImpressionsDataObjectPkgs = (
  pkg,
  resortLocation,
  checkoutDate,
  checkInDate,
  adultsCount,
  kidsCount,
  currentPosition,
  purchaseLocation,
  customList,
  customD30,
  upsellRecommendationData
) => {
  let returnObj = {
    name: pkg.title,
    id: pkg.packageCode,
    price: pkg.amount,
    brand: 'great wolf',
    category: 'package',
    variant: pkg.packageType ? pkg.packageType[0]?.trim() : '',
    list: customList ? customList : 'suites',
    position: currentPosition,
    dimension27: pkg.flags && pkg.flags[RECOMMENDED_FOR_YOU] ? 'recommended' : '',
    dimension28: '',
    dimension29: resortLocation,
    dimension30: customD30 ? customD30 : 'suites',
    dimension31: GwDatesWrapper.diff(checkoutDate, checkInDate, 'days'),
    dimension32: adultsCount,
    dimension33: kidsCount,
    dimension34: purchaseLocation || 'suite-checkout',
    dimension35: GwDatesWrapper.format(checkInDate, DATE_FORMATS.default),
    dimension36: GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default)
  };
  if (optimizelyReturnZSRecommendation && upsellRecommendationData?.analyticalGroup !== undefined) {
    applyAgentProperty(returnObj, upsellRecommendationData);
  }
  return returnObj;
};

export const getDayPassBaseObject = (reservation, locationDisplayName, locationState) => {
  return {
    city: locationDisplayName,
    region: locationState,
    item_code: reservation.reservationType,
    item_group: 'WPF',
    room_type: DAY_PASSES_ROOM_TYPE[reservation.reservationType] || '',
    room_name: reservation.product.name,
    value: reservation.costSummary.total
  };
};

export const getBaseSuiteObject = (reservation, locationDisplayName, locationState, suiteCategory) => {
  const numberOfNights = GwDatesWrapper.diff(reservation.departure, reservation.arrival, 'days');
  return {
    checkout_date: GwDatesWrapper.format(reservation.departure, DATE_FORMATS.default),
    num_children: reservation.guests.children.filter(children => children >= 2)?.length,
    num_infants: reservation.guests.children.filter(children => children < 2)?.length,
    children_ages: reservation.guests.children.length,
    city: locationDisplayName,
    region: locationState,
    room_nights: numberOfNights,
    promo_code: reservation.offerCode,
    room_type: suiteCategory,
    room_name: reservation.product.name
  };
};

export const getEventBaseObject = (event, reservation, currencyCode) => {
  const numberOfNights = GwDatesWrapper.diff(reservation.departure, reservation.arrival, 'days');
  const isDayPass = reservation.reservationType.includes('PASS');
  const averageRate = !isDayPass
    ? reservation.product.totalPrice / numberOfNights
    : reservation.product.totalPrice / reservation.guests.quantity;
  return {
    [EVENT_PARAMS.fbEventId]: uuidv4(),
    event,
    transaction_id: reservation.id,
    checkin_date: GwDatesWrapper.format(reservation.arrival, DATE_FORMATS.default),
    num_travelers: reservation.guests.adults + reservation.guests.children.length,
    num_adults: reservation.guests.adults,
    value: reservation.costSummary.total,
    average_room_rate: averageRate.toFixed(2),
    currency: currencyCode,
    country: reservation.payment.billingAddress.country || null
  };
};

export const buildProductClickEnhancedECommerceObject = (
  kidsAges,
  suite,
  resortLocation,
  checkoutDate,
  checkinDate,
  adultsCount,
  kidsCount,
  currentPosition,
  purchaseLocation
) => {
  return {
    event: PRODUCT_SCOPES.productClick,
    resortLocation: resortLocation,
    ecommerce: {
      currencyCode: 'USD',
      click: {
        actionField: {
          list: 'suites'
        },
        products: [
          {
            ...buildProductEnhancedECommerce(
              suite,
              resortLocation,
              checkoutDate,
              checkinDate,
              adultsCount,
              kidsCount,
              suite.avgNightlyRate,
              purchaseLocation
            ),
            position: currentPosition
          }
        ]
      }
    }
  };
};

export const buildAddToCartEnhancedEcommerceObject = (
  eventType,
  kidsAges,
  suite,
  resortLocation,
  checkoutDate,
  checkinDate,
  adultsCount,
  kidsCount,
  currentPosition,
  purchaseLocation,
  upsellRecommendationData
) => {
  return {
    event: eventType,
    resortLocation: resortLocation,
    purchaseLocation,
    ecommerce: {
      currencyCode: 'USD',
      [eventType === EVENT_CATEGORIES.GTM.addToCart ? 'add' : 'remove']: {
        actionField: {
          list: 'suites',
          position: currentPosition || '',
          coupon: suite.rateCode ?? ''
        },
        products: [
          {
            ...buildProductEnhancedECommerce(
              suite,
              resortLocation,
              checkoutDate,
              checkinDate,
              adultsCount,
              kidsCount,
              suite.totalRate,
              purchaseLocation,
              kidsAges,
              upsellRecommendationData
            ),
            quantity: 1
          }
        ]
      }
    }
  };
};

export const buildSearchObject = (
  resortLocation,
  searchType,
  bonusDealValue,
  searchLocation,
  checkinDate,
  checkoutDate,
  adultsCount,
  kidsCount,
  kidAges,
  discountCode
) => {
  return {
    event: EVENT_CATEGORIES.GTM.search,
    resortLocation: resortLocation,
    searchType: searchType,
    bonus_deal_value: bonusDealValue,
    searchLocation: searchLocation,
    arrival_date: checkinDate,
    departure_date: checkoutDate,
    numberOfAdults: adultsCount,
    numberOfKids: kidsCount,
    kidAges: kidAges,
    discountCode: discountCode,
    NumNights: GwDatesWrapper.diff(checkoutDate, checkinDate, 'days')
  };
};

const buildProductEnhancedECommerce = (
  suite,
  resortLocation,
  checkoutDate,
  checkinDate,
  adultsCount,
  kidsCount,
  price,
  purchaseLocation,
  kidsAges,
  upsellRecommendationData
) => {
  const nights = GwDatesWrapper.diff(checkoutDate, checkinDate, 'days');
  let returnObj = {
    name: suite.title,
    id: suite.suiteCode,
    price: price,
    brand: 'great wolf',
    category: RESERVATION_TYPE.SUITE,
    variant: suite.suiteCategory,
    dimension27: suite?.flags && suite?.flags.includes(RECOMMENDED_FOR_YOU) ? 'recommended' : '',
    dimension28: '',
    dimension29: resortLocation,
    dimension30: RESERVATION_TYPE.SUITE,
    dimension31: nights,
    dimension32: adultsCount,
    dimension33: kidsCount,
    dimension34: purchaseLocation || 'suite-checkout',
    dimension35: GwDatesWrapper.format(checkinDate, DATE_FORMATS.default),
    dimension36: GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default),
    dimension61: Number(suite?.maxOccupancy) || null, // product scope dimension for Maximum Suite Occupancy
    dimension62: kidsAges || [], // product scope dimension for Kids Ages
    coupon: suite.rateCode ?? ''
  };
  if (optimizelyReturnZSRecommendation && upsellRecommendationData?.analyticalGroup !== undefined) {
    applyAgentProperty(returnObj, upsellRecommendationData);
  }
  return returnObj;
};

export const getSignInEventObject = (action, label) => {
  if (!action || !label) return;
  return {
    event: GTM_EVENT.NAME.websiteVitals,
    category: EVENT_CATEGORIES.GTM.signIn,
    action: action,
    label: label
  };
};

export const getSignUpEventObject = (action, label, isLeadGenSignUp) => {
  if (!action || !label) return;
  const eventObject = {
    event: GTM_EVENT.NAME.websiteVitals,
    category: EVENT_CATEGORIES.GTM.signUp,
    action: action,
    label: label
  };
  if (label === GTM_EVENT.LABEL.google || label === GTM_EVENT.LABEL.greatWolf)
    return {
      ...eventObject,
      sign_up_location: isLeadGenSignUp ? 'subscribe' : 'sign up'
    };
  else return eventObject;
};

export const getLeadGenEventObject = (action, label) => {
  if (!action || !label) return;
  return {
    event: GTM_EVENT.NAME.websiteVitals,
    category: EVENT_CATEGORIES.GTM.leadgen,
    action: action,
    label: label
  };
};

export const getPasswordChangeEventObject = (action, label) => {
  if (!action || !label) return;
  return {
    event: GTM_EVENT.NAME.websiteVitals,
    category: EVENT_CATEGORIES.GTM.passwordChange,
    action: action,
    label: label
  };
};

export const getRaFSignUpEventObject = () => {
  if (sessionStorage.getItem(SessionStorageKeys.REFER_A_FRIEND)) {
    const MemberId = getSpecificItem('MemberId', 'referFriend');

    if (typeof MemberId === 'string' && MemberId) {
      const lst = MemberId.slice(-1);
      const action = +lst % 2 === 0 ? GTM_EVENT.ACTION.signUpAttemptRaF50 : GTM_EVENT.ACTION.signUpAttemptRaF25;
      return getSignUpEventObject(action, GTM_EVENT.LABEL.greatWolf);
    }
  }
};

/**
 * Build enhanced ecommerce object for Checkout steps
 * @param {Object} suite Suite redux object
 * @param {Object} adultsCount Adults count redux object
 * @param {Object} kidsCount Kids count redux object
 * @param {Object} kidsAges Kids ages redux object
 * @param {Object} checkinDateSelection Checkin date selection redux object
 * @param {Object} checkoutDateSelection Checkout date selection redux object
 * @param {Object} resortLocation Resort location redux object
 * @param {number} step Current step at checkout page
 * @param {boolean} isUserLoggedIn User status
 * @param {string} option Payment type
 * @returns Object
 */
export const buildCheckoutEventObject = (
  suite,
  adultsCount,
  kidsCount,
  kidsAges,
  checkinDateSelection,
  checkoutDateSelection,
  resortLocation,
  step,
  isUserLoggedIn,
  option = ''
) => {
  return {
    event: EVENT_CATEGORIES.GTM.checkoutStep,
    resortLocation: resortLocation,
    loggedinStatus: isUserLoggedIn,
    ecommerce: {
      currencyCode: 'USD',
      checkout: {
        actionField: {
          step,
          option
        },
        products: [
          {
            ...buildProductEnhancedECommerce(
              suite,
              resortLocation,
              checkoutDateSelection,
              checkinDateSelection,
              adultsCount,
              kidsCount,
              suite.totalRate,
              undefined,
              kidsAges
            ),
            quantity: 1
          }
        ]
      }
    }
  };
};

/**
 * Build enhanced ecommerce object for add/removal cart
 * @param {string} eventType Event type one of: add-to-cart, remove-from-cart
 * @param {Object} eventPackage Object of the new or removed package
 * @param {Object} packagesTable Packages planPackages redux object -- packagesTableJson
 * @param {Object} guests Guest redux object
 * @param {Object} dates Dates redux object
 * @param {Object} resortLocation Resort location redux object
 * @param {string} purchaseLocation Location of the event on the Booking Flow
 * @returns Object
 */
export const buildPackageCartEventObject = (
  eventType,
  eventPackage,
  packagesTable,
  guests,
  dates,
  resortLocation,
  qtyDiff = null,
  purchaseLocation = 'standard purchase',
  currentPathLocation = '',
  suite = [],
  isCartOpenClose,
  upsellRecommendationData,
  entities
) => {
  const { adultsCount, kidsCount, kidsAges } = guests;
  const { checkinDateSelection: checkinDate, checkoutDateSelection: checkoutDate } = dates;
  const numberOfNights = checkoutDate !== undefined && GwDatesWrapper.diff(checkoutDate, checkinDate);

  // Get Products
  const products = eventPackage.map(pkg => {
    let { packageName, packageCode, amount, category, packageCategory, quantity } = pkg;
    // Check if is recommended
    const isRecommended = packagesTable && eventPackage ? isRecommendedPackage(eventPackage, packagesTable) : false;
    // Get package type
    packageCategory = packageCategory ? packageCategory : getPackageCategory(pkg, packagesTable)?.toLowerCase();

    // Get quanity -- for dining by package we should to perform one operation
    quantity = quantity
      ? quantity
      : qtyDiff
      ? qtyDiff
      : getPackageQuantity(pkg, packageCategory, guests, numberOfNights);

    category = category ? category : 'packages';

    let returnObj = {
      name: packageName,
      id: packageCode,
      price: amount, // the nightly rate. what the user sees on the room card
      brand: 'great wolf', // static
      category: category, // static
      variant: packageCategory, // one of: attractions, family, birthday, dining, lco, flextrip
      quantity: quantity,
      dimension27: '', // empty string
      dimension28: isRecommended ? 'recommended' : '', // if the recommended package, otherwise empty string
      dimension29: resortLocation,
      dimension30: 'packages', // static
      dimension31: numberOfNights, // number of nights
      dimension32: adultsCount, // number of adults
      dimension33: kidsCount, // number of kids
      dimension34: isCartOpenClose
        ? 'standard purchase - cart'
        : purchaseLocation
        ? purchaseLocation
        : currentPathLocation, // static
      dimension35: GwDatesWrapper.format(checkinDate, DATE_FORMATS.default), // arrival date
      dimension36: GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default),
      dimension61: Number(suite?.selectedSuite?.maxOccupancy) || null, // product scope dimension for Maximum Suite Occupancy
      dimension62: kidsAges || [], // product scope dimension for Kids Ages
      coupon: entities?.manageReservationDetails?.data?.offerCode ?? suite?.selectedSuite?.rateCode ?? ''
    };
    if (optimizelyReturnZSRecommendation && upsellRecommendationData?.analyticalGroup !== undefined) {
      applyAgentProperty(returnObj, upsellRecommendationData);
    }
    return returnObj;
  });

  //const { packageCode, packageName, amount } = eventPackage;

  return {
    event: eventType, // one of: add-to-cart, remove-from-cart
    resortLocation: resortLocation,
    purchaseLocation: isCartOpenClose ? 'standard purchase - cart' : purchaseLocation,
    ecommerce: {
      currencyCode: 'USD',
      [eventType === EVENT_CATEGORIES.GTM.addToCart ? 'add' : 'remove']: {
        actionField: {
          list: 'packages',
          position: '',
          coupon: entities?.manageReservationDetails?.data?.offerCode ?? suite?.selectedSuite?.rateCode ?? ''
        },
        products: products
      }
    }
  };
};

export const rateCalendarGA = (action, label) => {
  if (!action || !label) return;
  return {
    event: GTM_EVENT.NAME.websiteVitals,
    category: EVENT_CATEGORIES.GTM.rateCalendar,
    action: action,
    label: label
  };
};

export const OFFERS_ACTIONS = {
  ADD: 'add',
  REMOVE: 'remove'
};

export const offerEventBuilder = (action, offerInfo) => {
  return {
    event: `bonus_deal_click_${action}`,
    resortLocation: offerInfo.resortLocation, //custom dimension for resort location that we can easily filter EE events by
    bonus_deal_name: offerInfo.name, //ex. July Bonus,
    bonus_deal_value: offerInfo.value //ex. 50 points,
  };
};

/**
 * Build enhanced ecommerce object for reservation cancellation
 * @param {Object} eventProps Object with relevant data for event
 * @param {Object} resortLocation Resort location redux object
 * @param {string} cancellationData Data related to the cancellation response
 * @returns Object
 */
export const buildCancelReservationEventObject = (
  eventProps,
  resortLocation,
  cancellationData,
  purchaseLocation = 'standard purchase'
) => {
  const {
    suiteName,
    suiteCode,
    suiteTotalPrice,
    reservationId,
    adultsCount,
    kidsCount,
    kidsAges,
    rateCode,
    maxOccupancy,
    checkinDate,
    checkoutDate,
    paymentType,
    packages,
    tax,
    resortFee,
    total
  } = eventProps;

  const numberOfNights = checkoutDate !== undefined && GwDatesWrapper.diff(checkoutDate, checkinDate);

  const { totalRefundAmount, cancelNumber, cancellationFeeAmount } = cancellationData;

  const productPackages = packages.map(pkg => ({
    name: pkg.description,
    id: pkg.packageCode,
    price: 0,
    brand: GTM_EVENT.LABEL.greatWolfLowerCase,
    category: 'packages',
    variant: pkg.packageType,
    quantity: pkg.quantity,
    coupon: rateCode,
    dimension31: numberOfNights, //product scope dimension for number of nights
    dimension32: adultsCount, //product scope dimension for number of adults
    dimension33: kidsCount, //product scope dimension for number of kids
    dimension34: PRODUCT_SCOPES.standardPurchase, //product scope dimension for add to cart location (pre vs post transaction)
    dimension35: checkinDate,
    dimension36: checkoutDate,
    dimension63: paymentType,
    dimension68: -pkg.unitAmount
  }));

  const products = [
    {
      name: suiteName,
      id: suiteCode,
      price: suiteTotalPrice / numberOfNights, // the nightly rate. what the user sees on the room card
      brand: 'great wolf', // static
      category: 'suites', // static
      variant: 'standard', // one of: attractions, family, birthday, dining, lco, flextrip
      quantity: 1,
      coupon: rateCode ?? '',
      dimension31: numberOfNights, // number of nights
      dimension32: adultsCount, // number of adults
      dimension33: kidsCount, // number of kids
      dimension34: purchaseLocation, // static
      dimension35: GwDatesWrapper.format(checkinDate, DATE_FORMATS.default), // arrival date
      dimension36: GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default),
      dimension61: maxOccupancy || null, // product scope dimension for Maximum Suite Occupancy
      dimension62: kidsAges || [], // product scope dimension for Kids Ages
      dimension63: paymentType,
      dimension68: -suiteTotalPrice
    },
    ...productPackages
  ];

  const refundAmount = total ? -Number(total) : 0;

  return {
    event: 'refund', // one of: add-to-cart, remove-from-cart
    resortLocation: resortLocation,
    purchaseLocation: purchaseLocation,
    ecommerce: {
      currencyCode: 'USD',
      refund: {
        actionField: {
          id: uuidv4(),
          affiliation: 'great wolf',
          revenue: totalRefundAmount,
          shipping: -resortFee,
          tax: -tax,
          coupon: rateCode,
          voyagerPointsDiscount: 0,
          offersPointsDiscount: 0,
          SIP: 2,
          confirmationNumber: reservationId,
          refundAmount,
          cancellationFee: cancellationFeeAmount,
          cancellationConfimationNumber: cancelNumber
        },
        products: products
      }
    }
  };
};

const voyagersAnalyticsSignInEventDefaultParameters = {
  sign_in_location: 'voyager_booking_flow',
  sign_in_group: '',
  military_status: ''
};

const getSignInVoyagersEventObject = (eventName, label) => {
  if (!label) return;
  return {
    ...voyagersAnalyticsSignInEventDefaultParameters,
    event: eventName,
    label
  };
};

const getSignUpVoyagersEventObject = (eventName, label) => {
  if (!label) return;
  return {
    sign_up_location: 'voyager_booking_flow',
    event: eventName,
    label
  };
};

export const getSignInAttemptEventObjectVoyagers = label =>
  getSignInVoyagersEventObject(GTM_EVENT.NAME.signInAttempt, label);
export const getSignInSuccessEventObjectVoyagers = label =>
  getSignInVoyagersEventObject(GTM_EVENT.NAME.signInSuccess, label);
export const getSignInFailureEventObjectVoyagers = label =>
  getSignInVoyagersEventObject(GTM_EVENT.NAME.signInFailure, label);
export const getSignUpAttemptEventObjectVoyagers = label =>
  getSignUpVoyagersEventObject(GTM_EVENT.NAME.signUpAttempt, label);
export const getSignUpSuccessEventObjectVoyagers = label =>
  getSignUpVoyagersEventObject(GTM_EVENT.NAME.signUpSuccess, label);
export const getSignUpFailureEventObjectVoyagers = label =>
  getSignUpVoyagersEventObject(GTM_EVENT.NAME.signUpFailure, label);

export const getBirthdayPartySearchObject = (
  resortLocation,
  searchLocation,
  partyDate,
  partyAdults,
  partyKids,
  partyInfants,
  partySpectators,
  partyTime
) => {
  return {
    event: EVENT_CATEGORIES.GTM.search,
    resortLocation,
    searchType: 'birthday',
    bonus_deal_value: '',
    searchLocation: `${searchLocation} - birthday party`,
    arrival_date: partyDate,
    departure_date: '',
    numberOfAdults: partyAdults,
    numberOfKids: partyKids,
    numberOfInfants: partyInfants,
    numberOfSpectators: partySpectators,
    kidAges: [],
    discountCode: '',
    NumNights: '',
    party_time: partyTime
  };
};

const getBirthdayPartyProductObject = (
  resortLocation,
  searchLocation,
  partyDate,
  partyAdults,
  partyKids,
  partyInfants,
  partySpectators,
  partyTime,
  productTitle,
  productId,
  productPrice
) => {
  return {
    name: productTitle,
    id: productId,
    price: productPrice,
    brand: 'great wolf',
    category: 'birthday party',
    variant: 'birthday party',
    quantity: 1,
    dimension27: '',
    dimension28: '',
    dimension29: resortLocation, //product scope dimension for resort
    dimension30: 'packages', //product scope dimension for item category
    dimension31: 1, //product scope dimension for number of nights
    dimension32: partyAdults, //product scope dimension for number of adults
    dimension33: partyKids, //product scope dimension for number of kids
    dimension34: `${searchLocation} - birthday party`, //product scope dimension for add location
    dimension35: partyDate, //party date
    dimension36: '',
    dimension61: '', //product scope dimension for max occupancy
    dimension62: [], //product scope dimension for kids ages
    coupon: '',
    dimension64: partyInfants, // product scope dimension for number of infants
    dimension65: partySpectators, // product scope dimension for number of spectators
    dimension67: partyTime // product scope dimension for party time"
  };
};

export const getBirthdayPartySelectObject = (
  eventType,
  resortLocation,
  searchLocation,
  partyDate,
  partyAdults,
  partyKids,
  partyInfants,
  partySpectators,
  partyTime,
  productTitle,
  productId,
  productPrice
) => {
  return {
    event: eventType,
    resortLocation,
    purchaseLocation: `${searchLocation} - birthday party`,
    ecommerce: {
      currencyCode: 'USD',
      [eventType === EVENT_CATEGORIES.GTM.addToCart ? 'add' : 'remove']: {
        actionField: {
          list: 'packages',
          position: '',
          coupon: ''
        },
        products: [
          getBirthdayPartyProductObject(
            resortLocation,
            searchLocation,
            partyDate,
            partyAdults,
            partyKids,
            partyInfants,
            partySpectators,
            partyTime,
            productTitle,
            productId,
            productPrice
          )
        ]
      }
    }
  };
};

export const getPromoSearchCustomView = recommendedPromo => ({
  event: EVENT_CATEGORIES.GTM.customView,
  viewCategory: 'promo error',
  viewType: recommendedPromo
});

export const getPromoErrorEventObject = (
  resortLocation,
  checkinDate,
  checkoutDate,
  numberOfAdults,
  kidsAges,
  recommendedPromo
) => ({
  event: EVENT_CATEGORIES.GTM.search,
  resortLocation,
  searchType: 'promo code error',
  bonus_deal_value: '',
  searchLocation: 'standard purchase',
  arrival_date: checkinDate,
  departure_date: checkoutDate,
  numberOfAdults,
  numberOfKids: kidsAges?.length ?? 0,
  numberOfInfants: '',
  numberOfSpectators: '',
  kidsAges,
  discountCode: recommendedPromo,
  NumNights: GwDatesWrapper.diff(checkoutDate, checkinDate, 'days'),
  party_time: ''
});
